import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { X, Plus, CreditCard, Banknote } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

interface Payment {
  id: string;
  method: string;
  amount: number;
  reference_number?: string;
  notes?: string;
}

interface SplitPaymentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  totalAmount: number;
  onConfirm: (payments: Payment[]) => void;
}

const paymentMethods = [
  { value: 'cash', label: 'Cash', icon: Banknote },
  { value: 'credit_card', label: 'Credit Card', icon: CreditCard },
  { value: 'debit_card', label: 'Debit Card', icon: CreditCard },
  { value: 'bank_transfer', label: 'Bank Transfer', icon: CreditCard },
  { value: 'cheque', label: 'Cheque', icon: CreditCard },
  { value: 'other', label: 'Other', icon: CreditCard },
];

export function SplitPaymentDialog({ isOpen, onClose, totalAmount, onConfirm }: SplitPaymentDialogProps) {
  const currencySymbol = useCurrencySymbol();
  const [payments, setPayments] = useState<Payment[]>([
    { id: '1', method: '', amount: 0 }
  ]);

  const totalPaid = payments.reduce((sum, payment) => sum + payment.amount, 0);
  const remaining = totalAmount - totalPaid;
  const amountToCollect = Math.max(0, remaining);
  const amountToReturn = Math.max(0, totalPaid - totalAmount);

  const addPayment = () => {
    setPayments([
      ...payments,
      { 
        id: Date.now().toString(), 
        method: '', 
        amount: Math.max(0, remaining) 
      }
    ]);
  };

  const removePayment = (id: string) => {
    if (payments.length > 1) {
      setPayments(payments.filter(p => p.id !== id));
    }
  };

  const updatePayment = (id: string, field: keyof Payment, value: any) => {
    setPayments(payments.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  const handleConfirm = () => {
    // Validate payments
    if (payments.some(p => !p.method)) {
      toast.error('Please select payment method for all rows');
      return;
    }

    if (payments.some(p => p.amount <= 0)) {
      toast.error('All payment amounts must be greater than 0');
      return;
    }

    if (amountToCollect > 0.01) {
      toast.error(`Remaining amount must be paid: ${currencySymbol}${amountToCollect.toFixed(2)}`);
      return;
    }

    onConfirm(payments);
  };

  const handleClose = () => {
    setPayments([{ id: '1', method: '', amount: 0 }]);
    onClose();
  };

  useEffect(() => {
    if (isOpen && payments.length === 1) {
      setPayments([{ id: '1', method: '', amount: totalAmount }]);
    }
  }, [isOpen, totalAmount]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Split Payment - {currencySymbol}{totalAmount.toFixed(2)}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {payments.map((payment, index) => {
            const IconComponent = paymentMethods.find(m => m.value === payment.method)?.icon || Banknote;
            
            return (
              <Card key={payment.id}>
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:items-end">
                    <div className="md:col-span-4">
                      <Label>Payment Method</Label>
                      <Select
                        value={payment.method}
                        onValueChange={(value) => updatePayment(payment.id, 'method', value)}
                      >
                        <SelectTrigger>
                          <div className="flex items-center gap-2">
                            <IconComponent className="h-4 w-4" />
                            <SelectValue />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          {paymentMethods.map(method => (
                            <SelectItem key={method.value} value={method.value}>
                              <div className="flex items-center gap-2">
                                <method.icon className="h-4 w-4" />
                                {method.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="md:col-span-3">
                      <Label>Amount</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={payment.amount || ''}
                        onChange={(e) => updatePayment(payment.id, 'amount', parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                      />
                    </div>

                    <div className="md:col-span-4">
                      <Label>Reference (Optional)</Label>
                      <Input
                        value={payment.reference_number || ''}
                        onChange={(e) => updatePayment(payment.id, 'reference_number', e.target.value)}
                        placeholder="Card #, Check #, etc."
                      />
                    </div>

                    <div className="md:col-span-1 md:flex md:justify-end">
                      {payments.length > 1 && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => removePayment(payment.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <Button
              variant="outline"
              onClick={addPayment}
              disabled={amountToCollect <= 0}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Payment Method
            </Button>

            <div className="text-right">
              <div className="text-sm text-gray-600">
                Paid: {currencySymbol}{totalPaid.toFixed(2)} / {currencySymbol}{totalAmount.toFixed(2)}
              </div>
              {amountToCollect > 0 ? (
                <div className="text-lg font-semibold text-red-600">
                  Remaining: {currencySymbol}{amountToCollect.toFixed(2)}
                </div>
              ) : amountToReturn > 0 ? (
                <div className="text-lg font-semibold text-blue-600">
                  Return to customer: {currencySymbol}{amountToReturn.toFixed(2)}
                </div>
              ) : (
                <div className="text-lg font-semibold text-green-600">Complete</div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={amountToCollect > 0.01}
            className="bg-green-600 hover:bg-green-700"
          >
            Process Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
